import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Row,
  Spinner,
  Stack,
} from 'react-bootstrap';
import {
  deliveriesService,
  getDeliveriesUnreadSummary,
  markDeliveryArchived,
  markDeliveryRead,
  markDeliveryRemoved,
} from '../../services';
import {
  MessageDelivery,
  MessageDeliveryState,
  MessageDeliverySummary,
} from '../../types/mensageria';

type DeliveryFilter = 'all' | 'unread' | 'archived';

const FILTER_OPTIONS: { value: DeliveryFilter; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'unread', label: 'Não lidas' },
  { value: 'archived', label: 'Arquivadas' },
];

const STATE_LABELS: Record<MessageDeliveryState, string> = {
  DELIVERED: 'Não lida',
  READ: 'Lida',
  ARCHIVED: 'Arquivada',
  REMOVED: 'Removida',
};

const STATE_VARIANTS: Record<MessageDeliveryState, string> = {
  DELIVERED: 'info',
  READ: 'success',
  ARCHIVED: 'secondary',
  REMOVED: 'danger',
};

const formatTimestamp = (value?: string) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const truncateBody = (text?: string, limit = 260) => {
  if (!text) {
    return '—';
  }
  return text.length > limit ? `${text.slice(0, limit)}…` : text;
};

export const MensageriaPage: React.FC = () => {
  const [deliveries, setDeliveries] = useState<MessageDelivery[]>([]);
  const [filter, setFilter] = useState<DeliveryFilter>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<MessageDeliverySummary | null>(null);
  const [mutatingDeliveryId, setMutatingDeliveryId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadDeliveries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await deliveriesService.list({ page_size: 100, ordering: '-delivered_at' });
      const filtered = response.results.filter((item) => !item.removed_at);
      setDeliveries(filtered);
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
      setError('Não foi possível carregar suas mensagens no momento.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const data = await getDeliveriesUnreadSummary();
      setSummary(data);
    } catch (err) {
      console.error('Falha ao buscar resumo de mensagens:', err);
    }
  }, []);

  useEffect(() => {
    loadDeliveries();
    loadSummary();
  }, [loadDeliveries, loadSummary]);

  const counts = useMemo(() => {
    const unread = deliveries.filter((item) => item.state === 'DELIVERED').length;
    const archived = deliveries.filter((item) => item.state === 'ARCHIVED').length;
    return {
      total: deliveries.length,
      unread,
      archived,
    };
  }, [deliveries]);

  const filteredDeliveries = useMemo(() => {
    if (filter === 'unread') {
      return deliveries.filter((item) => item.state === 'DELIVERED');
    }
    if (filter === 'archived') {
      return deliveries.filter((item) => item.state === 'ARCHIVED');
    }
    return deliveries;
  }, [deliveries, filter]);

  const handleAction = useCallback(
    async (delivery: MessageDelivery, action: 'read' | 'archive' | 'remove') => {
      setActionError(null);
      setMutatingDeliveryId(delivery.id);
      try {
        let updated: MessageDelivery;
        if (action === 'read') {
          updated = await markDeliveryRead(delivery.id);
        } else if (action === 'archive') {
          updated = await markDeliveryArchived(delivery.id);
        } else {
          updated = await markDeliveryRemoved(delivery.id);
        }
        setDeliveries((current) =>
          current
            .map((item) => (item.id === updated.id ? updated : item))
            .filter((item) => (action === 'remove' ? item.removed_at == null : true)),
        );
        await loadSummary();
      } catch (err) {
        console.error('Erro ao atualizar mensagem:', err);
        setActionError('Não foi possível atualizar o status da mensagem.');
      } finally {
        setMutatingDeliveryId(null);
      }
    },
    [loadSummary],
  );

  return (
    <Stack gap={4}>
      <div>
        <h2 className="mb-1">Mensagens do seu perfil</h2>
        <p className="text-muted mb-0">
          Centralize as comunicações enviadas para você e gerencie o histórico em um único
          lugar.
        </p>
      </div>

      <Row className="g-3">
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <Card.Title className="h6">Mensagens totais</Card.Title>
              <div className="display-6 fw-bold">{counts.total}</div>
              <div className="text-muted small">Todas as mensagens recebidas.</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <Card.Title className="h6">Não lidas</Card.Title>
              <div className="display-6 fw-bold">
                {summary?.unread ?? counts.unread}
              </div>
              <div className="text-muted small">Mensagens aguardando sua atenção.</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <Card.Title className="h6">Arquivadas</Card.Title>
              <div className="display-6 fw-bold">{counts.archived}</div>
              <div className="text-muted small">Mensagens guardadas para consulta.</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm border-0">
        <Card.Body className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
          <div className="d-flex flex-column">
            <Card.Title className="h6 mb-1">Filtrar mensagens</Card.Title>
            <span className="text-muted small">Use as opções abaixo para segmentar o feed.</span>
          </div>
          <ButtonGroup>
            {FILTER_OPTIONS.map((option) => (
              <Button
                key={option.value}
                variant={option.value === filter ? 'primary' : 'outline-secondary'}
                onClick={() => setFilter(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={loadDeliveries}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Atualizando
              </>
            ) : (
              'Atualizar'
            )}
          </Button>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}
      {actionError && <Alert variant="warning">{actionError}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : filteredDeliveries.length === 0 ? (
        <Alert variant="info" className="text-center">
          Nenhuma mensagem encontrada para o filtro selecionado.
        </Alert>
      ) : (
        filteredDeliveries.map((delivery) => (
          <Card key={delivery.id} className="shadow-sm border-0">
            <Card.Body>
              <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
                <div>
                  <Card.Title className="mb-1">{delivery.message.title}</Card.Title>
                  <div className="text-muted small">
                    {delivery.message.category ?? 'Sem categoria'} •{' '}
                    {formatTimestamp(delivery.delivered_at)}
                  </div>
                </div>
                <Badge bg={STATE_VARIANTS[delivery.state]} pill>
                  {STATE_LABELS[delivery.state]}
                </Badge>
              </div>
              <Card.Text className="text-muted small mt-3 mb-3">
                {truncateBody(delivery.message.body)}
              </Card.Text>
              <Stack direction="horizontal" gap={2} className="flex-wrap">
                {delivery.state === 'DELIVERED' && (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={mutatingDeliveryId === delivery.id}
                    onClick={() => handleAction(delivery, 'read')}
                  >
                    {mutatingDeliveryId === delivery.id ? 'Processando…' : 'Marcar como lida'}
                  </Button>
                )}
                {delivery.state !== 'ARCHIVED' && (
                  <Button
                    size="sm"
                    variant="outline-warning"
                    disabled={mutatingDeliveryId === delivery.id}
                    onClick={() => handleAction(delivery, 'archive')}
                  >
                    {mutatingDeliveryId === delivery.id ? 'Processando…' : 'Arquivar'}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline-danger"
                  disabled={mutatingDeliveryId === delivery.id}
                  onClick={() => handleAction(delivery, 'remove')}
                >
                  {mutatingDeliveryId === delivery.id ? 'Processando…' : 'Remover'}
                </Button>
              </Stack>
            </Card.Body>
          </Card>
        ))
      )}
    </Stack>
  );
};
